import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  LogOut,
  Trash2,
  CheckCircle,
  XCircle,
  Users,
  Briefcase,
  MessageSquare,
  LayoutDashboard,
  RefreshCw,
  Inbox,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("admin_token");
}

function authHeader() {
  return { Authorization: `Bearer ${getToken()}` };
}

const SOURCE_CONFIG = {
  all: { label: "All Submissions", icon: LayoutDashboard, color: "#2C4A77" },
  client: { label: "Aged Care Homes", icon: Briefcase, color: "#2563eb" },
  career: { label: "PCA Applicants", icon: Users, color: "#7c3aed" },
  contact: { label: "Contact", icon: MessageSquare, color: "#059669" },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, client: 0, career: 0, contact: 0, new: 0 });
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSource, setActiveSource] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const headers = authHeader();
      const params = {};
      if (activeSource !== "all") params.source = activeSource;
      if (statusFilter === "new") params.contacted = false;
      if (statusFilter === "contacted") params.contacted = true;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [statsRes, subsRes] = await Promise.all([
        axios.get(`${API}/api/admin/submissions/stats`, { headers }),
        axios.get(`${API}/api/admin/submissions`, { headers, params }),
      ]);
      setStats(statsRes.data);
      setSubmissions(subsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_email");
        navigate("/admin");
        return;
      }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [activeSource, search, statusFilter, dateFrom, dateTo, navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleToggleContacted = async (id, currentStatus) => {
    try {
      await axios.patch(
        `${API}/api/admin/submissions/${id}?contacted=${!currentStatus}`,
        {},
        { headers: authHeader() }
      );
      toast.success(currentStatus ? "Marked as New" : "Marked as Contacted");
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/api/admin/submissions/${id}`, {
        headers: authHeader(),
      });
      toast.success("Submission deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin");
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const sourceBadge = (source) => {
    const colors = {
      client: "bg-blue-100 text-blue-800",
      career: "bg-violet-100 text-violet-800",
      contact: "bg-emerald-100 text-emerald-800",
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          colors[source] || "bg-gray-100 text-gray-700"
        }`}
      >
        {source}
      </span>
    );
  };

  return (
    <div className="min-h-screen flex bg-care-light" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside
        className="w-60 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col"
        data-testid="admin-sidebar"
      >
        <div className="p-5 border-b border-gray-100">
          <img
            src="/logo-wordmark.png"
            alt="PrimeShift Group"
            className="h-7 w-auto"
          />
          <p className="text-xs text-gray-400 mt-1.5">Admin Dashboard</p>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {Object.entries(SOURCE_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const isActive = activeSource === key;
            const count =
              key === "all" ? stats.total : stats[key] || 0;
            return (
              <button
                key={key}
                onClick={() => setActiveSource(key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
                style={isActive ? { background: "#2C4A77" } : {}}
                data-testid={`sidebar-${key}`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1 text-left">{cfg.label}</span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-100">
          <div className="px-3 py-2 text-xs text-gray-400 truncate mb-2">
            {localStorage.getItem("admin_email")}
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Header bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1
              className="text-xl font-semibold text-care-blue"
              data-testid="dashboard-title"
            >
              {SOURCE_CONFIG[activeSource].label}
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {stats.new} new submission{stats.new !== 1 ? "s" : ""} awaiting
              review
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="gap-2"
            data-testid="refresh-button"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </Button>
        </div>

        {/* Stats cards */}
        <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total", value: stats.total, color: "#2C4A77" },
            { label: "Aged Care", value: stats.client, color: "#2563eb" },
            { label: "PCA Apps", value: stats.career, color: "#7c3aed" },
            { label: "Contact", value: stats.contact, color: "#059669" },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-lg border border-gray-200 p-4"
              data-testid={`stat-${s.label.toLowerCase()}`}
            >
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                {s.label}
              </p>
              <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="px-6 pb-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="search-input"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
            </SelectContent>
          </Select>

          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-[150px] h-9 text-sm"
            placeholder="From"
            data-testid="date-from-filter"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-[150px] h-9 text-sm"
            placeholder="To"
            data-testid="date-to-filter"
          />

          {(search || statusFilter !== "all" || dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
                setDateFrom("");
                setDateTo("");
              }}
              className="text-xs text-gray-500"
              data-testid="clear-filters-button"
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="px-6 pb-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                Loading...
              </div>
            ) : submissions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400" data-testid="empty-state">
                <Inbox className="w-10 h-10 mb-3" />
                <p className="font-medium">No submissions found</p>
                <p className="text-sm mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/80">
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-care-blue">
                      Name
                    </TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-care-blue">
                      Email
                    </TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-care-blue">
                      Phone
                    </TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-care-blue">
                      Details
                    </TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-care-blue">
                      Source
                    </TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-care-blue">
                      Date
                    </TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-care-blue">
                      Status
                    </TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-right text-care-blue">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((sub) => (
                    <TableRow key={sub._id} data-testid={`submission-row-${sub._id}`}>
                      <TableCell className="font-medium text-gray-900">
                        {sub.name}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {sub.email}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {sub.phone || "—"}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm max-w-[200px] truncate">
                        {sub.company || sub.requirements || sub.message || sub.experience || "—"}
                      </TableCell>
                      <TableCell>{sourceBadge(sub.source)}</TableCell>
                      <TableCell className="text-gray-500 text-sm whitespace-nowrap">
                        {formatDate(sub.created_at)}
                      </TableCell>
                      <TableCell>
                        {sub.contacted ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 hover:bg-emerald-100">
                            Contacted
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-0 hover:bg-amber-100">
                            New
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleToggleContacted(sub._id, sub.contacted)
                            }
                            className={`h-8 w-8 p-0 ${
                              sub.contacted
                                ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            }`}
                            title={
                              sub.contacted
                                ? "Mark as New"
                                : "Mark as Contacted"
                            }
                            data-testid={`toggle-status-${sub._id}`}
                          >
                            {sub.contacted ? (
                              <XCircle className="w-4 h-4" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(sub._id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                            data-testid={`delete-${sub._id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {!loading && submissions.length > 0 && (
            <p className="text-xs text-gray-400 mt-3 text-right">
              Showing {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
